import {useCallback, useEffect, useRef, useState} from 'react';
import {message} from 'antd';
import {useMutation} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import {
  PRESIGN_COMPLAINT_ATTACHMENT,
  PresignComplaintAttachmentResponse,
} from '../../../graphql/complaintTask/mutations';
import {ComplaintAttachmentInput} from '../../../graphql/complaintTask/types';
import {translateGraphQLError} from '../../../utils/graphqlUtils';

/** 单个附件上限（PC / 手机统一 5MB，10-14 确认） */
export const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;

export interface PendingAttachment extends ComplaintAttachmentInput {
  /** 图片本地预览地址（object URL，切换任务 / 卸载时释放） */
  previewUrl?: string;
}

/**
 * 处理说明附件上传（PC 作业端、手机版共用）：预签名 → 直传对象存储 → 记下 fileKey，提交解决时一并带上。
 * resetKey 变化（如切换任务）时清空已选附件；切换前还没传完的文件作废，不会落到新任务上。
 * 多个文件并发上传时，全部传完 uploading 才为 false（提交按钮据此置灰，避免漏带附件）。
 */
export const useComplaintAttachmentUpload = (resetKey: unknown) => {
  const {t} = useTranslation();
  const [files, setFiles] = useState<PendingAttachment[]>([]);
  const [inFlight, setInFlight] = useState(0);
  const previewUrls = useRef<string[]>([]);
  /** 每次清空 +1；上传完成时代数不一致说明已切换任务，结果丢弃 */
  const generation = useRef(0);
  const [presign] = useMutation<PresignComplaintAttachmentResponse>(PRESIGN_COMPLAINT_ATTACHMENT);

  const clear = useCallback(() => {
    generation.current += 1;
    previewUrls.current.forEach((u) => URL.revokeObjectURL(u));
    previewUrls.current = [];
    setFiles([]);
  }, []);

  useEffect(() => clear, [resetKey, clear]);

  const upload = async (file: File) => {
    if (file.size > MAX_ATTACHMENT_BYTES) {
      message.error(t('complaintTask.work.fileTooLarge'));
      return;
    }
    const gen = generation.current;
    setInFlight((n) => n + 1);
    try {
      const contentType = file.type || 'application/octet-stream';
      const res = await presign({variables: {fileName: file.name, contentType, size: file.size}});
      const target = res.data?.presignComplaintAttachment;
      if (!target) throw new Error('presign failed');
      // 不带 Content-Type 头：R2 桶 CORS 的 AllowedHeaders 为空，带 image/jpeg 等头会触发预检并被 403 拒绝（与门牌免拍上传同做法）；
      // file.slice() 得到 type 为空的 Blob，浏览器也不会自动补该头，且零拷贝
      const put = await fetch(target.uploadUrl, {method: 'PUT', body: file.slice()});
      if (!put.ok) throw new Error(`upload failed: ${put.status}`);
      if (gen !== generation.current) return;
      const previewUrl = contentType.startsWith('image/') ? URL.createObjectURL(file) : undefined;
      if (previewUrl) previewUrls.current.push(previewUrl);
      setFiles((prev) => [...prev, {fileKey: target.fileKey, fileName: file.name, contentType, previewUrl}]);
    } catch (e) {
      if (gen === generation.current) message.error(translateGraphQLError(e, 'complaintTask.work.uploadFailed'));
    } finally {
      setInFlight((n) => n - 1);
    }
  };

  const remove = (fileKey: string) => setFiles((prev) => {
    const dropped = prev.find((f) => f.fileKey === fileKey);
    if (dropped?.previewUrl) {
      URL.revokeObjectURL(dropped.previewUrl);
      previewUrls.current = previewUrls.current.filter((u) => u !== dropped.previewUrl);
    }
    return prev.filter((f) => f.fileKey !== fileKey);
  });

  const toInputs = (): ComplaintAttachmentInput[] =>
    files.map(({fileKey, fileName, contentType}) => ({fileKey, fileName, contentType}));

  return {files, uploading: inFlight > 0, upload, remove, clear, toInputs};
};
